import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { ApiService } from '../services/api';
import { PublicProductSearchResponse, WebSearchResult } from '../types/models';
import { useAppContext } from '../context/AppContext';
import { trackGoal } from '../utils/metrika';

const formatPrice = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'цена не указана';
  }
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 2,
  }).format(value);
};

const getGateMessage = (data: PublicProductSearchResponse | null) => {
  if (!data) {
    return null;
  }
  if (data.requires_login) {
    return 'Эта поисковая сессия уже привязана к аккаунту. Войдите, чтобы продолжить.';
  }
  if (data.requires_registration) {
    return 'Бесплатный лимит публичного поиска исчерпан. Войдите или зарегистрируйтесь, чтобы продолжить.';
  }
  if (data.error === 'too_many_requests') {
    return 'Слишком много запросов подряд. Попробуйте еще раз через минуту.';
  }
  return 'Поиск временно недоступен. Попробуйте еще раз позже.';
};

const SearchResultsSkeleton: React.FC = () => (
  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" aria-hidden="true">
    {[0, 1, 2, 3].map((item) => (
      <div
        key={item}
        className="bg-white border border-gray-200 rounded-lg p-4 shadow-md overflow-hidden"
      >
        <div className="animate-pulse">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="h-4 bg-blue-100 rounded w-28 mb-3" />
              <div className="h-5 bg-gray-200 rounded w-full mb-2" />
              <div className="h-5 bg-gray-100 rounded w-2/3" />
            </div>
            <div className="h-7 bg-green-50 rounded w-16 flex-shrink-0" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <div className="h-3 bg-gray-100 rounded w-12 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
            <div>
              <div className="h-3 bg-gray-100 rounded w-10 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
          </div>
          <div className="h-4 bg-blue-100 rounded w-20" />
        </div>
      </div>
    ))}
  </div>
);

const exampleQueries = [
  'Авт. выкл. 3P C25 6кА',
  'ВВГнг(A)-LS 5х10',
  'УЗО 4P 40А 30мА',
  'Лоток перф. 100х50',
];

const PublicProductSearch: React.FC = () => {
  const { isAuthenticated } = useAppContext();
  const [query, setQuery] = useState('');
  const [brand, setBrand] = useState('');
  const [results, setResults] = useState<WebSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [gateResponse, setGateResponse] = useState<PublicProductSearchResponse | null>(null);
  const [lastSearchQuery, setLastSearchQuery] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading) {
      setLoadingSeconds(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setLoadingSeconds((current) => current + 1);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isLoading]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    const normalizedBrand = brand.trim();
    if (normalizedQuery.length < 3) {
      setErrorMessage('Введите запрос от 3 символов.');
      return;
    }

    setIsLoading(true);
    setLoadingSeconds(0);
    setErrorMessage(null);
    setGateResponse(null);
    setLastSearchQuery(null);
    setResults([]);
    trackGoal('quick_search_submit', {
      source: 'homepage_quick_search',
      query_length: normalizedQuery.length,
      has_brand: Boolean(normalizedBrand),
      authenticated: isAuthenticated,
    });

    try {
      const response = await ApiService.publicProductSearch(
        normalizedQuery,
        normalizedBrand || undefined
      );
      setResults(response.results || []);
      setLastSearchQuery(normalizedQuery);
      trackGoal('quick_search_result', {
        source: 'homepage_quick_search',
        result_count: response.results?.length || 0,
        authenticated: isAuthenticated,
        remaining_free_requests:
          typeof response.remaining_free_requests === 'number'
            ? response.remaining_free_requests
            : null,
      });
    } catch (error: unknown) {
      setResults([]);
      if (axios.isAxiosError<PublicProductSearchResponse>(error)) {
        if (error.code === 'ECONNABORTED') {
          trackGoal('quick_search_error', {
            source: 'homepage_quick_search',
            error_type: 'timeout',
            authenticated: isAuthenticated,
          });
          setErrorMessage('Проверка заняла больше времени, чем ожидалось. Попробуйте другую формулировку или загрузите файл для полной обработки.');
          return;
        }
        const data = error.response?.data || null;
        trackGoal('quick_search_error', {
          source: 'homepage_quick_search',
          error_type: data?.error || error.response?.status || 'axios_error',
          requires_login: Boolean(data?.requires_login),
          requires_registration: Boolean(data?.requires_registration),
          authenticated: isAuthenticated,
        });
        setGateResponse(data);
        setErrorMessage(getGateMessage(data));
      } else {
        trackGoal('quick_search_error', {
          source: 'homepage_quick_search',
          error_type: 'unknown',
          authenticated: isAuthenticated,
        });
        setErrorMessage('Поиск временно недоступен. Попробуйте еще раз позже.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto text-left">
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
      >
        <div className="px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-2">
            Быстрая проверка
          </div>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
            Проверьте одну позицию перед загрузкой файла
          </h2>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
            Вставьте строку из спецификации, чтобы увидеть несколько возможных товаров.
            Для полной обработки Excel, PDF или схемы загрузите документ выше.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 pt-5 pb-6">
          <label className="block md:col-span-7">
            <span className="sr-only">Запрос</span>
            <div className="h-12 flex items-center gap-3 px-4 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  if (lastSearchQuery) {
                    setLastSearchQuery(null);
                  }
                }}
                className="min-w-0 flex-1 h-full border-0 p-0 focus:outline-none text-gray-900 placeholder:text-gray-400"
                placeholder="Например: крышка с заземлением на лоток осн.50 L3000"
                disabled={isLoading}
              />
            </div>
          </label>
          <label className="block md:col-span-3">
            <span className="sr-only">Бренд</span>
            <input
              value={brand}
              onChange={(event) => {
                setBrand(event.target.value);
                if (lastSearchQuery) {
                  setLastSearchQuery(null);
                }
              }}
              className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              placeholder="Бренд, если важен"
              disabled={isLoading}
            />
          </label>
          <button
            type="submit"
            disabled={isLoading || query.trim().length < 3}
            className="h-12 md:col-span-2 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-5 rounded-lg font-semibold transition-colors whitespace-nowrap"
          >
            {isLoading ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5" />
            )}
            {isLoading ? 'Проверяем' : 'Проверить'}
          </button>
        </div>
        {isLoading && (
          <div className="px-5 pb-4 text-sm text-gray-600">
            {loadingSeconds < 30
              ? 'Проверяем позицию. Поиск может занять десятки секунд.'
              : 'Запрос еще выполняется: LLM и поиск по источникам могут идти одну-две минуты.'}
          </div>
        )}
        <div className="px-5 pb-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Примеры строк
          </div>
          <div className="flex flex-wrap gap-2">
            {exampleQueries.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => {
                  setQuery(example);
                  setErrorMessage(null);
                  setGateResponse(null);
                  setLastSearchQuery(null);
                  setResults([]);
                }}
                disabled={isLoading}
                className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      </form>

      {errorMessage && (
        <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            {gateResponse?.requires_registration || gateResponse?.requires_login ? (
              <LockClosedIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
            )}
            <p className="text-sm text-amber-900">{errorMessage}</p>
          </div>
          {(gateResponse?.requires_registration || gateResponse?.requires_login) && (
            <Link
              to="/login"
              onClick={() => trackGoal('login_click', { source: 'quick_search_gate' })}
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Войти
            </Link>
          )}
        </div>
      )}

      {isLoading && <SearchResultsSkeleton />}

      {!isLoading && !errorMessage && lastSearchQuery && results.length === 0 && (
        <div className="mt-5 rounded-lg border border-dashed border-gray-300 bg-white px-5 py-6 text-center shadow-sm">
          <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-gray-400 mb-3" />
          <div className="text-base font-semibold text-gray-900 mb-1">
            Ничего не найдено
          </div>
          <p className="text-sm text-gray-600">
            По запросу «{lastSearchQuery}» не удалось подобрать товары. Попробуйте уточнить
            наименование, артикул или бренд.
          </p>
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.map((item, index) => (
            <div
              key={`${item.article}-${index}`}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-md"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-2 text-sm text-blue-700 font-semibold mb-1">
                    <TagIcon className="h-4 w-4" />
                    {item.article || 'артикул не указан'}
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug">
                    {item.name}
                  </h3>
                </div>
                {item.is_persist && (
                  <span className="flex-shrink-0 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">
                    в базе
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div>
                  <div className="text-gray-500">Бренд</div>
                  <div className="font-medium text-gray-900">{item.brand || 'не указан'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Цена</div>
                  <div className="font-medium text-gray-900">{formatPrice(item.price)}</div>
                </div>
              </div>
              {item.source_url && (
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Источник
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicProductSearch;
